<template>
  <div :class="b(modifiers)">
    <div :class="b('content')">
      <div
        v-if="hasStaticContent"
        :class="b('static')"
      >
        <div :class="b('title')">
          <span
            v-if="prefix"
            :class="b('prefix')"
          >
            {{ prefix }}
          </span>
          {{ title }}
        </div>
      </div>

      <div :class="b('slider-wrapper')">
        <div
          ref="slider"
          :class="b('slider', { static: this.static })"
          :style="sliderStyles"
        >
          <div
            ref="stage"
            :class="b('stage', { static: this.static })"
            class="embla__container"
          >
            <!-- @slot Returns the Array of slides if available. -->
            <slot :slides="slides">
              <c-slider-item-1
                v-for="(slide, index) in slides"
                :key="index"
                :data="slide"
              />
            </slot>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showControls"
      :class="b('controls', { navigationAlwaysShown })"
    >
      <c-slide-pagination
        :class="b('pagination')"
        :pages="noOfPages"
        :active-page="activePage"
        @select="scrollTo"
      />

      <div :class="b('navigation', { alwaysShown: navigationAlwaysShown })">
        <e-icon-cta
          ref="previous"
          :class="b('navigation-button', { previous: true })"
          :disabled="disablePrev"
          :aria-label="$t('c-slider-1.previousLabel')"
          :icon="matchTheme(themeConfig.icons.navigatePrevious)"
          @click="previous"
        />
        <e-icon-cta
          ref="next"
          :class="b('navigation-button', { next: true })"
          :disabled="disableNext"
          :aria-label="$t('c-slider-1.nextLabel')"
          :icon="matchTheme(themeConfig.icons.navigateNext)"
          @click="next"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import EmblaCarousel from 'embla-carousel';
  import cSlidePagination from '@/components/c-slide-pagination.vue';
  import cSliderItem1 from '@/components/c-slider-item-1.vue';
  import eIconCta from '@/elements/e-icon-cta.vue';
  import propScale from '@/helpers/prop.scale.js';
  import mixinSliderInit from '@/mixins/slider-init.js';
  import themes from '@/mixins/themes.ts';

  /**
   * Renders a slider component.
   *
   * Note: the height of each slide and therefore the calculation of the slider height
   * can be changed by applying 'align-self' on the slide component.
   */
  export default {
    name: 'c-slider-1',

    components: {
      cSliderItem1,
      eIconCta,
      cSlidePagination,
    },
    mixins: [mixinSliderInit, themes],

    props: {
      /**
       * Allows to extend or overwrite the default Embla settings. @see https://davidcetinkaya.github.io/embla-carousel/api#options
       */
      emblaOptions: {
        type: Object,
        default: () => ({}),
      },

      /**
       * Allows to change the active class for a single slide item.
       */
      activeClass: {
        type: String,
        default: 'is-active',
      },

      /**
       * An Array of slider data objects. See used slider item component.
       */
      slides: {
        type: Array,
        default: null,
      },

      /**
       * Allows to select a style variant of the slider.
       *
       * - overflow: The slider initially overflows to the right. Uses full width.
       * - fullwidth: The slider uses 100vw.
       */
      variant: {
        type: String,
        default: 'overflow',
        validator(value) {
          return ['overflow', 'fullwidth', 'auto-width'].includes(value);
        },
      },

      /**
       * Allows to set a title for the slider.
       */
      title: {
        type: String,
        default: null,
      },

      /**
       * Allows to set a title prefix for the slider.
       */
      prefix: {
        type: String,
        default: null,
      },

      /**
       * If enabled, navigation elements will always be visible despite media queries.
       */
      navigationAlwaysShown: {
        type: Boolean,
        default: false,
      },

      /**
       * If enabled, the slider will not be draggable (bounce) when there is just a single page to display. Effectively disabeling interaction when number of pages is 1.
       */
      staticWhenFitting: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines the padding of component.
       */
      padding: propScale(500, [0, 500]),

      /**
       * Sets the alignment of the internal slider.
       */
      alignment: {
        type: String,
        default: 'auto',
        validator(value) {
          return ['auto', 'start', 'center'].includes(value);
        },
      },
    },
    data() {
      return {
        /**
         * @type {EmblaCarousel} The related slider instance.
         */
        sliderInstance: null,

        /**
         * @type {Number} Holds the index of the active slide.
         */
        activeSlide: 0,

        /**
         * @type {Number} Holds the amount of total slides.
         */
        noOfSlides: 0,

        /**
         * @type {Number} Holds the amount of slide pages.
         */
        noOfPages: 0,

        /**
         * @type {Number} Holds the currently active page.
         */
        activePage: 0,

        /**
         * @type {Number} Holds the amount of sliders per page.
         */
        slidesPerPage: 0,

        /**
         * @type {Boolean} Defines if the controls should be rendered or not.
         */
        showControls: false,

        /**
         * @type {Number} Holds the height of the current slide if 'height: adjust' is active.
         */
        sliderHeight: null,

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          icons: {
            navigatePrevious: {
              default: 'i-navigate-previous',
              sagemax: 'i-navigate-previous-square',
              'private-label': 'i-navigate-previous-square',
            },
            navigateNext: {
              default: 'i-navigate-next',
              sagemax: 'i-navigate-next-square',
              'private-label': 'i-navigate-next-square',
            },
          },
        },
      };
    },

    computed: {
      /**
       * Checks if the slider is supposed to show static content.
       *
       * @returns {Boolean}
       */
      hasStaticContent() {
        return !!this.title;
      },

      /**
       * Calculates inline style for the slider.
       *
       * @returns {Object}
       */
      sliderStyles() {
        return {
          height: this.sliderHeight ? `${this.sliderHeight}px` : null,
        };
      },

      /**
       * Returns an object of BEM modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          variant: this.hasStaticContent ? 'static' : this.variant,
          padding: this.padding,
          theme: this.theme,
        };
      },

      /**
       * Defines if the previous button should be disabled.
       *
       * @returns {Boolean}
       */
      disablePrev() {
        return this.activeSlide === 0;
      },

      /**
       * Defines if the next button should be disabled.
       *
       * @returns {Boolean}
       */
      disableNext() {
        return this.activePage === this.noOfPages - 1;
      },

      /**
       * Defines if the component is static.
       *
       * @returns {Boolean}
       */
      static() {
        return this.staticWhenFitting && this.noOfPages <= 1;
      },
    },
    watch: {
      /**
       * Refresh Embla instance on variant change.
       */
      'variant': function () {
        this.sliderInstance.reInit(this.getEmblaOptions());
      },

      /**
       * Refresh Embla instance if screen size changes.
       */
      '$viewport.isSm': function () {
        this.sliderInstance.reInit(this.getEmblaOptions());
      },

      /**
       * Refresh Embla instance if alignment changes.
       */
      'alignment': function () {
        this.sliderInstance.reInit(this.getEmblaOptions());
      },

      /**
       * Update pagination if number of slides changes.
       */
      'noOfSlides': function () {
        this.$nextTick(() => {
          // Was required because DOM was initial not ready.
          this.updatePagination();
        });
      },

      /**
       * Refresh Embla instance if static changes.
       */
      'static': function () {
        this.sliderInstance.reInit(this.getEmblaOptions());
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() { },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() { },
    // unmounted() {},

    methods: {
      /**
       * Returns Embla options based on the current viewport.
       *
       * @returns {Object}
       */
      getEmblaOptions() {
        let align = this.alignment;

        if (align === 'auto') {
          align = this.$viewport.isSm ? 'start' : 'center';
        }

        return {
          align,
          containScroll: this.$viewport.isSm ? 'keepSnaps' : '',
          speed: this.variant === 'fullwidth' ? 12 : 10,
          inViewThreshold: 0.6, // The default of 0.5 resulted in invalid calculations on mobile.
          draggable: !this.static,
          ...this.emblaOptions,
        };
      },

      /**
       * Creates an instance of the Embla slider.
       */
      // NOTE: gets called via mixinSliderInit
      // eslint-disable-next-line vue/no-unused-properties
      createSlider() {
        if (this.sliderInstance) {
          return;
        }

        this.sliderInstance = new EmblaCarousel(this.$refs.slider, this.getEmblaOptions());

        this.sliderInstance.on('init', this.onInit);
        this.sliderInstance.on('select', this.onSelect); // select is also triggerd on drag events.
        this.sliderInstance.on('resize', this.onResize);

        // Since fonts have an impact on slide width and height, we need to update the slider when it finished loading.
        document.fonts?.ready.then(this.onFontReady);
      },

      /**
       * Callback for the fonts ready event.
       */
      onFontReady() {
        this.sliderInstance.reInit(this.getEmblaOptions());
        this.updateHeight();
      },

      /**
       * Event handler for the resize event of the slider instance.
       */
      onResize() {
        this.updateSlidesPerPage();
        this.updatePagination();
        this.updateControlVisibility();
        this.updateHeight();
      },

      /**
       * Event handler for the sliders init event.
       */
      onInit() {
        this.onResize();
        this.updateActiveSlide();
      },

      /**
       * Event handler for the sliders select event.
       */
      onSelect() {
        this.updateActiveSlide();
        this.updateActivePage();
        this.updateHeight();
      },

      /**
       * Updates the height of the slider to meet the active slides height.
       */
      updateHeight() {
        this.$nextTick(() => {
          // Makes sure the DOM updated before measuring.
          let height = null;

          if (this.sliderInstance.slidesInView().length === 1) {
            const slides = this.sliderInstance.slideNodes();
            const selected = this.sliderInstance.selectedScrollSnap();

            height = slides[selected].offsetHeight;
          }

          this.sliderHeight = height;
        });
      },

      /**
       * Scrolls to the page with the given index.
       *
       * @param {Number} page - The desired slide index.
       */
      scrollTo(page) {
        const pageBySlide = page * this.slidesPerPage;

        this.sliderInstance?.scrollTo(pageBySlide);
      },

      /**
       * Scrolls to the next page.
       */
      next() {
        this.scrollTo(this.activePage + 1);
      },

      /**
       * Scrolls to the previous page.
       */
      previous() {
        this.scrollTo(this.activePage - 1);
      },

      /**
       * Recalculates the amount of slides per page.
       */
      updateSlidesPerPage() {
        this.slidesPerPage = this.sliderInstance?.slidesInView().length || 1;
      },

      /**
       * Updates the pagination (including number of pages).
       */
      updatePagination() {
        this.noOfPages = this.noOfSlides ? this.getScrollSnapList().length : 1;

        this.updateActivePage();
      },

      /**
       * Updates the active page.
       */
      updateActivePage() {
        this.activePage = Math.ceil((this.sliderInstance?.selectedScrollSnap() || 0) / this.slidesPerPage);
      },

      /**
       * Checks if items are outside of the viewport and en-/disables controls.
       */
      updateControlVisibility() {
        this.showControls = this.getScrollSnapList().length > 1;
      },

      /**
       * Updates the slides active classes.
       */
      updateActiveSlide() {
        const slides = this.sliderInstance.slideNodes();
        const previous = this.sliderInstance.previousScrollSnap();
        const selected = this.sliderInstance.selectedScrollSnap();

        slides[previous]?.classList.remove(this.activeClass);
        slides[selected]?.classList.add(this.activeClass);

        this.activeSlide = selected;
        this.noOfSlides = slides.length;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  c-slider-1 {
    @extend %fullwidth--content;
    @extend %component-spacing;

    display: flex;
    padding-right: functions.rem(28px + 8px); // Mobile padding.
    padding-left: functions.rem(27px + 8px); // Mobile padding.
    overflow: hidden;

    @include mixins.media(sm) {
      padding-right: 0;
      padding-left: 0;
    }

    &[variant='static'] {
      @include mixins.media(sm) {
        padding-left: calc(40% + #{functions.rem(variables.$grid-gutter--lg + variables.$grid-whitespace--md + 3px)});
      }

      @include mixins.media(md) {
        padding-left: functions.rem(360px + variables.$grid-gutter--lg + variables.$grid-whitespace--lg + 3px);
      }

      > * {
        margin-top: functions.rem(90px); // Title spacing

        @include mixins.media(sm) {
          margin-top: 0;
        }
      }
    }

    &[variant='overflow'] {
      @include mixins.media(sm) {
        padding-left: functions.rem(variables.$grid-whitespace--md);
      }

      @include mixins.media(md) {
        padding-left: functions.rem(variables.$grid-whitespace--lg);
      }
    }
  }

  .c-slider-1 {
    $this: &;

    @extend %component-spacing;

    overflow: hidden; // Required to prevent scrollbars from showing.

    @media print {
      break-inside: avoid;
    }

    &:not(&--variant-auto-width) {
      @extend %fullwidth--content;
    }

    &__content {
      @include mixins.media(sm) {
        display: flex;
      }
    }

    &__static {
      flex: 0 0 auto;
      padding: functions.rem(0 (28px + 8px) 0 (27px + 8px)); // Mobile padding.

      @include mixins.media(sm) {
        width: 40%;
        margin-right: 3px; // Makes slide border visible.
        padding: functions.rem(0 variables.$grid-gutter--lg 0 variables.$grid-whitespace--md);
      }

      @include mixins.media(md) {
        box-sizing: content-box;
        width: functions.rem(360px);
        padding-left: functions.rem(variables.$grid-whitespace--lg);
      }
    }

    &__title {
      @extend %heading-h2;

      margin-bottom: 0.3em;
    }

    &__prefix {
      @include mixins.font(variables.$font-size--16);

      display: block;
      margin-bottom: functions.rem(variables.$spacing--25);
      color: variables.$color-grayscale--100;
    }

    &__slider-wrapper {
      flex: 1 1 auto;
      overflow: hidden; // Prevents element from extending beyond flex container.

      @include mixins.media(sm) {
        padding: functions.rem(0 variables.$grid-whitespace--md);
      }

      @include mixins.media(md) {
        padding: functions.rem(0 variables.$grid-whitespace--lg);
      }

      #{$this}__static + & {
        padding-left: 0;
      }
    }

    &__slider {
      transition: height variables.$transition-duration--500 ease;
    }

    &__stage {
      display: flex;

      &--static {
        transform: none !important; // stylelint-disable-line declaration-no-important
        user-select: none;
      }
    }

    &__controls {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: functions.rem(variables.$grid-whitespace--xs);

      @include mixins.media(md) {
        justify-content: flex-end;
        margin-right: functions.rem(variables.$grid-whitespace--lg);
      }

      &--navigation-always-shown {
        justify-content: flex-end;
      }
    }

    &__pagination,
    &__navigation-button {
      // Margin was added to this elements, to also hide the margin when the controls are inactive.
      margin-top: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        margin-top: functions.rem(variables.$spacing--40);
      }
    }

    &__navigation {
      display: none;
      margin-left: functions.rem(variables.$spacing--40);

      @include mixins.media(md) {
        display: flex;
      }

      &--always-shown {
        display: flex;
      }
    }

    &__navigation-button {
      transition: fill variables.$transition-duration--200;
      color: variables.$color-primary--2;

      &:first-child {
        margin-right: functions.rem(variables.$spacing--25);
      }

      &:hover {
        color: variables.$color-primary--1;
      }

      .e-icon-sprite {
        width: functions.rem(64px);
        height: functions.rem(64px);
      }
    }

    &--variant-fullwidth,
    &--variant-auto-width {
      #{$this}__slider-wrapper {
        padding: 0;
      }
    }

    &--variant-auto-width {
      #{$this}__controls {
        margin-right: 0;
      }
    }

    &--padding-0 {
      padding: 0;
    }
  }

  c-slider-1,
  .c-slider-1 {
    $this: &;

    &--theme-private-label,
    &--theme-sagemax {
      #{$this}__navigation-button {
        transition: opacity variables.$transition-duration--200 ease-in-out;
        color: variables.$color-grayscale--0 !important; // stylelint-disable-line declaration-no-important

        &:hover,
        &:disabled {
          opacity: 0.4;
        }
      }
    }
  }
</style>
